"use client";

import React from "react";
import TrackedLink from "@components/TrackedLink";
import ScheduleCall from "@components/ScheduleCall";
import { track } from "@lib/track";
import { feeLabel } from "@lib/fees";

const ADULT = feeLabel("consultation");
const CHILD = feeLabel("childConsultation");

const WHAT_YOU_GET = [
  { label: "Full health history review", detail: "Medical history, current symptoms, genetics, diet, lifestyle and any existing test results — nothing overlooked" },
  { label: "Root cause assessment", detail: "We identify which systems are under stress and where to intervene first for the fastest, most meaningful relief" },
  { label: "Personalised plan", detail: "Practical nutrition, lifestyle and targeted supplement recommendations — with clear steps and timelines, not a generic handout" },
  { label: "Medication & supplement safety check", detail: "All interactions reviewed before anything is recommended" },
  { label: "Written notes & protocol", detail: "Everything from the session in writing so nothing gets lost or forgotten" },
  { label: "Clear next steps", detail: "You leave knowing exactly what to do and what to expect — not wondering where to start" },
];

const TESTIMONIALS = [
  {
    quote: "I've never felt so well, so grounded, and so in tune with myself.",
    body: "Since completing my breast cancer treatment last year, Nichola helped me detox and reclaim my health. I feel emotionally and spiritually stronger than ever.",
    name: "Lucie W.",
    tag: "Nutrition Through Cancer Treatment",
  },
  {
    quote: "I always feel amazing afterwards — my inflammation and Psoriasis improve every time.",
    body: "She has made something that seems quite overwhelming and complicated very accessible and simple. I come back to Niki again and again.",
    name: "Johnathan",
    tag: "Repeat Client",
  },
  {
    quote: "I have learned so much from Niki's expert guidance.",
    body: "Learning how to make the foods I need to support my gut and liver has been invaluable — something I can incorporate into my regular nutrition forever.",
    name: "Claire",
    tag: "Nutrition Programme",
  },
];

const HOW_WE_WORK = [
  {
    step: "1",
    title: "Information Gathering",
    items: [
      "Full medical history & current symptoms",
      "Goals, genetics, diet & lifestyle",
      "Review of existing tests (blood, hormones, stool, hair mineral, etc.)",
      "Medication & supplement interactions checked for safety",
    ],
  },
  {
    step: "2",
    title: "Root Cause Assessment",
    items: [
      "Identify which systems are under stress",
      "Decide where to intervene first for fastest relief",
    ],
  },
  {
    step: "3",
    title: "Personalised Plan",
    items: [
      "Nutrition, lifestyle & targeted supplements",
      "Clear steps, timelines and what to expect",
    ],
  },
  {
    step: "4",
    title: "Ongoing Support",
    items: [
      "Follow-ups to adjust & progress",
      "Optional Bodhi Club for regular check-ins",
    ],
  },
];

export default function ConsultationClient() {
  const onPlay = (label: string) => {
    try {
      track(
        "video_play",
        { label, component: "ConsultationPage.Video" },
        { event: "video_play", label, component: "ConsultationPage.Video" }
      );
    } catch {}
  };

  const scrollToScheduler: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
    e.preventDefault();
    document.getElementById("schedule-call")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="relative bg-[var(--color-brand)] text-[var(--text-on-dark)]">

      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <header className="mx-auto max-w-5xl px-5 md:px-10 py-12 md:py-20 text-center">
        <p className="text-xs md:text-sm uppercase tracking-widest opacity-70 mb-4 font-semibold">
          Initial Consultation — Niki Charnock
        </p>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight text-balance">
          A Proper Deep-Dive Into What's Actually Going On.
        </h1>
        <p className="mt-5 text-sm md:text-lg opacity-90 max-w-3xl mx-auto leading-relaxed text-pretty">
          Root cause assessment and a personalised plan — led by{" "}
          <strong>Niki Charnock, Naturopathic Nutritionist</strong>. One focused hour to understand
          your health history and map a clear way forward.
        </p>
        <div className="mt-8">
          <TrackedLink
            href="#schedule-call"
            onClick={scrollToScheduler}
            variant="solid"
            size="lg"
            color="base"
            event="cta_click"
            label="Consultation | Book Hero"
            component="ConsultationPage"
          >
            Book Your Consultation
          </TrackedLink>
        </div>
        <p className="mt-3 text-xs md:text-sm opacity-70">
          Adults {ADULT} · Children {CHILD} · Online or in person
        </p>
      </header>

      {/* ── BODY ─────────────────────────────────────────────────────────────── */}
      <main className="mx-auto max-w-5xl -mt-6 rounded-t-3xl bg-[var(--bg-surface)] px-5 md:px-10 pb-20 shadow-2xl text-[var(--text-on-light)]">

        {/* ── VIDEO ────────────────────────────────────────────────────────── */}
        <section className="pt-10 md:pt-14 text-center">
          <h2 className="text-xl md:text-2xl font-bold text-[var(--text-brand)]">What to Expect</h2>
          <p className="mt-2 text-[var(--text-on-light-muted)] max-w-md mx-auto text-sm md:text-base">
            A comprehensive, personalised assessment to uncover root causes and build a clear plan forward.
          </p>
          <div className="mt-6">
            <video
              controls
              preload="none"
              poster="/images/Niki_Charnock.webp"
              className="mx-auto w-full md:w-2/4 max-w-2xl rounded-2xl shadow-lg"
              onPlay={() => onPlay("Consultation Overview")}
            >
              <source src="/images/consultationCall.mp4" type="video/mp4" />
            </video>
            <p className="mt-3 text-sm text-[var(--text-on-light-muted)]">A quick overview of how your consultation works.</p>
          </div>
        </section>

        {/* ── WHAT YOU GET ─────────────────────────────────────────────────── */}
        <section className="mt-14 max-w-3xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-[var(--text-brand)] text-center mb-2">
            What's Included
          </h2>
          <p className="text-sm text-[var(--text-on-light-muted)] text-center mb-8">
            This is not a quick chat and a printed handout. Here's what actually happens:
          </p>
          <div className="space-y-3">
            {WHAT_YOU_GET.map((item, i) => (
              <div key={i} className="rounded-2xl border border-[var(--border-accent)] bg-[var(--bg-card)] p-4 shadow-sm flex gap-4 items-start">
                <span className="mt-0.5 text-green-600 font-bold shrink-0">✓</span>
                <div>
                  <p className="font-semibold text-sm text-[var(--text-brand)]">{item.label}</p>
                  <p className="text-sm text-[var(--text-on-light-muted)] mt-0.5">{item.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── HOW WE WORK TOGETHER ─────────────────────────────────────────── */}
        <section className="mt-14">
          <h2 className="text-xl md:text-2xl font-bold text-[var(--text-brand)] text-center mb-6">
            How We Work Together
          </h2>
          <div className="grid md:grid-cols-2 gap-5">
            {HOW_WE_WORK.map(({ step, title, items }) => (
              <div key={step} className="rounded-2xl border border-[var(--border-accent)] bg-[var(--bg-card)] p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-7 h-7 rounded-full bg-[var(--color-brand)] text-[var(--text-on-dark)] text-xs font-bold flex items-center justify-center shrink-0">
                    {step}
                  </span>
                  <h3 className="font-semibold text-[var(--text-brand)]">{title}</h3>
                </div>
                <ul className="text-sm list-disc pl-5 space-y-1 text-[var(--text-on-light-muted)]">
                  {items.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* ── TESTIMONIALS ─────────────────────────────────────────────────── */}
        <section className="mt-14">
          <h2 className="text-xl md:text-2xl font-bold text-[var(--text-brand)] text-center mb-2">
            What Clients Say
          </h2>
          <p className="text-sm text-[var(--text-on-light-muted)] text-center mb-8 max-w-2xl mx-auto">
            From clients working with Niki across her programmes.
          </p>
          <div className="grid md:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="rounded-2xl border border-[var(--border-accent)] bg-[var(--bg-card)] p-5 shadow-sm flex flex-col gap-3">
                <p className="text-sm font-semibold text-[var(--text-brand)] italic leading-snug">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <p className="text-sm text-[var(--text-on-light-muted)] leading-relaxed flex-1">
                  {t.body}
                </p>
                <div>
                  <p className="text-sm font-semibold text-[var(--text-brand)]">{t.name}</p>
                  <p className="text-xs text-[var(--text-on-light-muted)]">{t.tag}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── PRICING + SCHEDULER ──────────────────────────────────────────── */}
        <section id="schedule-call" className="mt-14 max-w-3xl mx-auto scroll-mt-24">
          <div className="rounded-2xl border-2 border-[var(--text-brand)] bg-[var(--bg-card)] p-6 shadow-md text-center mb-8">
            <p className="text-sm text-[var(--text-on-light-muted)] uppercase tracking-wider font-semibold mb-1">Investment</p>
            <p className="text-4xl font-extrabold text-[var(--text-brand)]">{ADULT}</p>
            <p className="text-sm text-[var(--text-on-light-muted)] mt-1">Adults · 60 minutes · Online or in person</p>
            <p className="text-sm text-[var(--text-on-light-muted)] mt-1">Children {CHILD}</p>
          </div>

          <ScheduleCall
            duration={60}
            meetingType="full-consultation"
            title="Initial Consultation"
            subtitle={`Adults ${ADULT} · Children ${CHILD}. We'll review your history, identify root causes, and create a practical, personalised plan.`}
            ctaText="Confirm & Book"
            componentName="ConsultationPage"
          />
        </section>

        {/* ── SECONDARY PATH ───────────────────────────────────────────────── */}
        <section className="mt-10 max-w-3xl mx-auto rounded-2xl border border-[var(--border-accent)] bg-[var(--bg-card)] p-6 text-center shadow-sm">
          <h2 className="text-base md:text-lg font-semibold text-[var(--text-brand)]">Not sure if it's the right fit?</h2>
          <p className="text-sm text-[var(--text-on-light-muted)] mt-2 mb-5 max-w-sm mx-auto">
            A free discovery call is the natural first step. Niki will ask a few questions and
            you'll both have a clear sense of the right path forward.
          </p>
          <TrackedLink
            href="/workwithme/discoverycall"
            variant="solid"
            size="md"
            color="base"
            event="cta_click"
            label="Consultation | Free Discovery Call"
            component="ConsultationPage"
          >
            Book a free discovery call →
          </TrackedLink>
        </section>

        <img
          src="/images/Niki_sig.webp"
          alt="Signature — Niki Charnock"
          className="mt-10 mx-auto"
          style={{ width: 150 }}
        />
      </main>

      {/* ── STICKY MOBILE CTA ────────────────────────────────────────────────── */}
      <div className="fixed inset-x-0 bottom-0 z-40 md:hidden border-t border-[var(--border-on-light)] bg-[var(--bg-surface)]/95 backdrop-blur supports-[backdrop-filter]:bg-[var(--bg-surface)]/70">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3">
          <span className="text-sm font-medium text-[var(--text-on-light)]">Ready to start?</span>
          <TrackedLink
            href="#schedule-call"
            onClick={scrollToScheduler}
            variant="solid"
            size="md"
            color="base"
            event="cta_click"
            label="Consultation | Book Sticky"
            component="ConsultationPage"
          >
            Book now
          </TrackedLink>
        </div>
      </div>
    </div>
  );
}
